import { useState, useEffect } from 'react';

/**
 * Hook to detect autobattler mode and disable player actions
 * @param {Object} gameState - Current game state
 * @returns {Object} - { isAutobattler: boolean, isSpectator: boolean }
 */
/**
 * Hook to detect autobattler mode and disable player actions
 * @param {Object} gameState - Current game state
 * @param {string} userId - Current user's ID (optional, for spectator detection)
 * @returns {Object} - { isAutobattler: boolean, isSpectator: boolean }
 */
export function useAutobattlerMode(gameState, userId = null) {
  const [isAutobattler, setIsAutobattler] = useState(false);
  const [isSpectator, setIsSpectator] = useState(false);

  useEffect(() => {
    if (!gameState) {
      setIsAutobattler(false);
      setIsSpectator(false);
      return;
    }

    const autobattlerMode = gameState.isAutobattler === true;
    setIsAutobattler(autobattlerMode);
    
    // Check if user is actually a spectator in autobattler mode
    // In autobattler games, players are bots (e.g., "1_autobattler_bot", "autobattler_opponent_bot")
    // The user watching is a spectator if they're not one of the bot players
    if (autobattlerMode) {
      if (userId) {
        // Normalize IDs to strings for comparison (userId might be number, spectatorUserId might be string)
        const userIdStr = String(userId);
        const spectatorUserIdStr = gameState.spectatorUserId ? String(gameState.spectatorUserId) : null;
        
        // Check if user is the designated spectator (compare as strings to handle type mismatch)
        const isDesignatedSpectator = spectatorUserIdStr === userIdStr;
        
        // Check if user is not in the players list (players are bots, not the user)
        const players = gameState.players || [];
        // Normalize player IDs to strings for comparison
        const playerIdsStr = players.map(p => String(p));
        const isNotAPlayer = !playerIdsStr.includes(userIdStr);
        
        // Also check if userId doesn't end with "_bot" (bot IDs end with "_bot")
        const isNotABot = !userIdStr.endsWith('_bot') && !userIdStr.endsWith('_autobattler_bot');
        const shouldBeSpectator = isDesignatedSpectator || (isNotAPlayer && isNotABot);
        
        console.log(`🔍 [useAutobattlerMode] Checking spectator status:`, {
          autobattlerMode,
          userId,
          userIdStr,
          spectatorUserId: gameState.spectatorUserId,
          spectatorUserIdStr,
          players: JSON.stringify(players),
          playerIdsStr: JSON.stringify(playerIdsStr),
          isDesignatedSpectator,
          isNotAPlayer,
          isNotABot,
          shouldBeSpectator
        });
        console.log(`🔍 [useAutobattlerMode] FINAL RESULT: shouldBeSpectator=${shouldBeSpectator}, isDesignatedSpectator=${isDesignatedSpectator}, isNotAPlayer=${isNotAPlayer}, isNotABot=${isNotABot}`);
        console.log(`🔍 [useAutobattlerMode] Setting isSpectator to: ${shouldBeSpectator}`);
        
        setIsSpectator(shouldBeSpectator);
      } else {
        // If no userId provided, assume spectator (backward compatibility)
        console.log(`🔍 [useAutobattlerMode] No userId provided, assuming spectator`);
        setIsSpectator(true);
      }
    } else {
      setIsSpectator(false);
    }
  }, [gameState, userId]);

  return {
    isAutobattler,
    isSpectator
  };
}

